// App.js
import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Navigate,
  Routes,
} from "react-router-dom";

import { createTheme, ThemeProvider } from "@mui/material";

import Role from "./Role";
import MFALite from "./MFALite";
import SimpleFileUpload from "./SimpleFileUpload";
import Reports from "./Reports";
import NotFoundPage from "./NotFoundPage";
import Login from "./Login";
import SignUp from "./SignUp";
import ServerStatus from "./ServerStatus";
import Users from "./Users";
import Companies from "./Companies";
import LicenseAgreements from "./LicenseAgreements";
import InputLibrary from "./InputLibrary";
import Input from "./Input";
import { MFALiteProvider } from "./MFALiteContext";
import { AppProvider } from "./AppContext";
import Profile from "./Profile";

import ProtectedRoute from "./ProtectedRoute";
import LicenseGuard from "./LicenseGuard";
import "./custom-styles.css";
import ForgotPassword from "./ForgotPassword";
import Usage from "./Usage";
import EmailVerification from "./EmailVerification";
import ResetPasswordDialog from "./ResetPasswordDialog";
import { useAuthStore } from "./store/authStore";
import { useRoleStore } from "./store/roleStore";
import License from "./License";
import CedexImport from "./CedexImport";
import RequireCedexAccess from "./components/RequireCedexAccess";
import Activity from "./activity/Activity";
import SqlDebug from "./SqlDebug";
import AuthSessions from "./AuthSessions";
import Runs from "./Runs";
import Evaluations from "./Evaluations";
import Projects from "./projects/Projects";
import ProjectDetail from "./projects/ProjectDetail";
import ApiClients from "./ApiClients";
import OAuthCallback from "./OAuthCallback";

const theme = createTheme({
  palette: {
    primary: {
      main: "#2F3F5C",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#3B82F6",
    },
  },
  shape: { borderRadius: 8 },
  typography: {
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    h4: { fontWeight: 700, letterSpacing: -0.3 },
    h5: { fontWeight: 600, letterSpacing: -0.2 },
    h6: { fontWeight: 600 },
    button: { textTransform: "none", fontWeight: 600 },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 8 },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: { borderRadius: 12 },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 12 },
      },
    },
  },
});

function App({ signOut }) {
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const token = useAuthStore(state => state.token);
  const user = useAuthStore(state => state.user);
  const fetchRoles = useRoleStore(state => state.fetchRoles);
  const appReady = useAuthStore(state => state.appReady);

  // Use user.roles for current user access control
  useEffect(() => {
    if (
      appReady &&
      isAuthenticated &&
      token &&
      typeof token === 'string' &&
      token !== 'null' &&
      token !== 'undefined' &&
      token.trim() !== ''
    ) {
      // Only fetch all roles for admin pages, not for user access control
      // fetchRoles(isAuthenticated, token); // Only needed for admin/role management
    }
  }, [isAuthenticated, token, fetchRoles, appReady, user]);

  return (
    <AppProvider>
      <ThemeProvider theme={theme}>
        <MFALiteProvider>
          <Router>
            <Routes>
              {/* Public Routes - No auth or license required */}
              <Route path="/" element={<Login />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<SignUp />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/verify/:verificationToken" element={<EmailVerification />} />
              <Route path="/reset-password/:resetToken" element={<ResetPasswordDialog />} />
              <Route path="/callback" element={<OAuthCallback />} />
              <Route path="/license" element={<License />} />
              
              {/* Routes that require license agreement */}
              <Route element={<LicenseGuard />}>
                {/* Protected Routes - require both auth and license agreement */}
                <Route
                  path="/profile"
                  element={<ProtectedRoute><Profile /></ProtectedRoute>}
                />
                <Route
                  path="/usage"
                  element={<ProtectedRoute><Usage /></ProtectedRoute>}
                />
                <Route
                  path="/users"
                  element={<ProtectedRoute><Users /></ProtectedRoute>}
                />
                <Route
                  path="/companies"
                  element={<ProtectedRoute><Companies /></ProtectedRoute>}
                />
                <Route
                  path="/license-agreements"
                  element={<ProtectedRoute><LicenseAgreements /></ProtectedRoute>}
                />
                <Route
                  path="/role-list"
                  element={<ProtectedRoute><Role /></ProtectedRoute>}
                />
                <Route
                  path="/mfalite"
                  element={<ProtectedRoute><MFALite signOut={signOut} /></ProtectedRoute>}
                />
                <Route
                  path="/file"
                  element={<ProtectedRoute><SimpleFileUpload signOut={signOut} /></ProtectedRoute>}
                />
                <Route
                  path="/user/:id"
                  element={<ProtectedRoute><Profile /></ProtectedRoute>}
                />
                <Route
                  path="/report-list"
                  element={<ProtectedRoute><Reports /></ProtectedRoute>}
                />
                <Route
                  path="/report-list/:userId/:reportId"
                  element={<ProtectedRoute><Reports /></ProtectedRoute>}
                />
                <Route
                  path="/report-list/:id"
                  element={<ProtectedRoute><Reports /></ProtectedRoute>}
                />
                <Route
                  path="/server-status"
                  element={<ProtectedRoute><ServerStatus /></ProtectedRoute>}
                />
                <Route
                  path="/activity"
                  element={<ProtectedRoute><Activity /></ProtectedRoute>}
                />
                {/* The Audit Log page became the Activity page's Audit trail tab. */}
                <Route path="/audit-log" element={<Navigate to="/activity" replace />} />
                <Route
                  path="/sql-debug"
                  element={<ProtectedRoute><SqlDebug /></ProtectedRoute>}
                />
                <Route
                  path="/auth-sessions"
                  element={<ProtectedRoute><AuthSessions /></ProtectedRoute>}
                />
                <Route
                  path="/runs"
                  element={<ProtectedRoute><Runs /></ProtectedRoute>}
                />
                <Route
                  path="/api-clients"
                  element={<ProtectedRoute><ApiClients /></ProtectedRoute>}
                />
                <Route
                  path="/input-library"
                  element={<ProtectedRoute><InputLibrary /></ProtectedRoute>}
                />
                <Route
                  path="/input"
                  element={<ProtectedRoute><Input /></ProtectedRoute>}
                />
                <Route
                  path="/input/:id"
                  element={<ProtectedRoute><Input /></ProtectedRoute>}
                />
                <Route
                  path="/cedex-import"
                  element={<ProtectedRoute><RequireCedexAccess><CedexImport /></RequireCedexAccess></ProtectedRoute>}
                />
                <Route
                  path="/cedex-import/:importId"
                  element={<ProtectedRoute><RequireCedexAccess><CedexImport /></RequireCedexAccess></ProtectedRoute>}
                />
                {/* /evaluations — Admin + InternalUser cross-report review
                    list. The page itself renders an access-denied alert when
                    the caller lacks the role, and the backend router
                    (roles=['Admin','InternalUser']) is the authoritative
                    gate; this route just needs an authenticated user. */}
                <Route
                  path="/evaluations"
                  element={<ProtectedRoute><Evaluations /></ProtectedRoute>}
                />
                {/* Project tracking — Admin + InternalUser; the pages render
                    an access-denied alert otherwise and the backend router
                    is the real gate. */}
                <Route
                  path="/projects"
                  element={<ProtectedRoute><Projects /></ProtectedRoute>}
                />
                <Route
                  path="/projects/:id"
                  element={<ProtectedRoute><ProjectDetail /></ProtectedRoute>}
                />
              </Route>

              {/* Catch-all 404 */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Router>
        </MFALiteProvider>
      </ThemeProvider>
    </AppProvider>
  );
}

export default App;
